// src/components/ads-admin/AdsSettingsModal.jsx
// Brand/Global settings for ADS Discovery — configurable AI prompts and models.

import React, { useEffect, useState } from "react";
import { settingsList, settingsUpsert } from "../../services/api";

const DEFAULT_INDIVIDUAL_PROMPT = `You are an advertising analyst. Analyze the digital ad below and return a JSON object.

AD DATA:
{ad_context}

Return ONLY valid JSON — no markdown, no explanation:
{
  "tone": "one of: professional | casual | emotional | urgent | humorous | authoritative | friendly",
  "main_message": "one sentence — the ad's core message",
  "target_audience": "inferred target demographic or psychographic",
  "key_claims": ["specific claim or promise 1", "claim 2"],
  "cta_analysis": "brief assessment of the call-to-action",
  "flags": ["concern 1 e.g. unsubstantiated claim — empty array if none"],
  "summary": "2-3 sentence overall assessment of ad quality and effectiveness"
}`;

const DEFAULT_CHAT_SYSTEM = `You are an advertising analyst. Analyze the provided ads for tone, messaging, claims, audience targeting, and compliance flags. Be concise and direct.`;

const DEFAULT_CHAT_AUTO_MESSAGE = `Please provide a comprehensive analysis of all selected ads. Identify patterns in creative format, hook types, tone, messaging strategy, call-to-action approaches, and visual themes. Highlight what makes certain ads stand out and flag any potential compliance concerns.`;

const DEFAULT_MODEL = "gpt-4.1";

const ANALYSIS_MODELS = [
  { id: "gpt-4.1",             label: "GPT-4.1 (recommended)" },
  { id: "gpt-5",               label: "GPT-5" },
  { id: "gpt-5-mini",          label: "GPT-5 mini (fast)" },
  { id: "gpt-4o-mini",         label: "GPT-4o mini (fast)" },
  { id: "sonar",               label: "Perplexity Sonar" },
  { id: "sonar-pro",           label: "Perplexity Sonar Pro" },
  { id: "sonar-reasoning",     label: "Perplexity Sonar Reasoning" },
  { id: "gemini-2.5-pro",      label: "Gemini 2.5 Pro" },
  { id: "gemini-2.5-flash",    label: "Gemini 2.5 Flash (fast)" },
];

const PROMPT_KEYS = {
  individual: "ads.prompt.individual_analysis",
  chat: "ads.prompt.chat_system",
  chat_auto: "ads.prompt.chat_auto_message",
};

const MODEL_KEYS = {
  individual: "ads.model.individual_analysis",
  bulk: "ads.model.bulk_analysis",
};

const THRESHOLD_KEY = "ads.chat.cost_alert_threshold";

const ALL_KEYS = [...Object.values(PROMPT_KEYS), ...Object.values(MODEL_KEYS), THRESHOLD_KEY];

function extractKey(items, key) {
  const row = (items || []).find(x => x?.key === key);
  return (row?.value ?? "").toString().trim();
}

function ReferencePrompt({ label, value }) {
  const [open, setOpen] = React.useState(false);
  if (!value) return null;
  return (
    <div style={{ marginBottom: 14, border: "1px solid #f3f4f6", borderRadius: 8, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", textAlign: "left", background: "#f8fafc", border: "none",
          padding: "7px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          fontSize: 11, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em",
        }}
      >
        <span style={{ fontSize: 10 }}>{open ? "▼" : "▶"}</span>
        {label}
      </button>
      {open && (
        <pre style={{
          fontSize: 11, color: "#6b7280", background: "#f8fafc",
          padding: "8px 10px", overflowX: "auto", whiteSpace: "pre-wrap",
          maxHeight: 160, overflow: "auto", margin: 0, borderTop: "1px solid #f3f4f6",
        }}>
          {value}
        </pre>
      )}
    </div>
  );
}

export default function AdsSettingsModal({ open, onClose, brandId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(null);

  // Active tab: "individual" | "chat" | "models"
  const [tab, setTab] = useState("individual");

  // Scope toggle: "global" | "brand" (only relevant when brandId is provided)
  const [editScope, setEditScope] = useState(brandId ? "brand" : "global");

  // Stored values (global + brand)
  const [globalVals, setGlobalVals] = useState({});
  const [brandVals, setBrandVals] = useState({});

  // Prompt editor value (for individual / chat tabs)
  const [editorVal, setEditorVal] = useState("");
  // Second editor — auto analysis message (only shown in chat tab)
  const [editorAutoVal, setEditorAutoVal] = useState("");
  // Cost alert threshold (chat tab)
  const [thresholdVal, setThresholdVal] = useState("1");

  // Model selector values (for models tab)
  const [indivModel, setIndivModel] = useState(DEFAULT_MODEL);
  const [bulkModel, setBulkModel]   = useState(DEFAULT_MODEL);

  const isBrand = editScope === "brand" && !!brandId;

  const defaults = { individual: DEFAULT_INDIVIDUAL_PROMPT, chat: DEFAULT_CHAT_SYSTEM, chat_auto: DEFAULT_CHAT_AUTO_MESSAGE };

  const activeKey = tab === "models" ? null : PROMPT_KEYS[tab === "chat" ? "chat" : tab];

  const effectiveVal = (tabKey) => {
    const key = PROMPT_KEYS[tabKey];
    const bv = brandVals[key] || "";
    const gv = globalVals[key] || "";
    return bv || gv || defaults[tabKey] || "";
  };

  const effectiveModel = (modelKey) => {
    const bv = brandVals[modelKey] || "";
    const gv = globalVals[modelKey] || "";
    return bv || gv || DEFAULT_MODEL;
  };

  const brandOverrides = (key) => isBrand && !!(brandVals[key] || "").trim();
  const source = (key) => {
    if (isBrand && (brandVals[key] || "").trim()) return "brand";
    if ((globalVals[key] || "").trim()) return "global";
    return "default";
  };

  // Reset + load on open
  useEffect(() => {
    if (!open) return;
    setTab("individual");
    setEditScope(brandId ? "brand" : "global");
    setErr(null);
    setSaved(null);

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [gRes, bRes] = await Promise.all([
          settingsList("global", null),
          isBrand ? settingsList("brand", brandId) : Promise.resolve({ items: [] }),
        ]);
        if (cancelled) return;
        const gi = gRes?.items || [];
        const bi = bRes?.items || [];
        const gv = {};
        const bv = {};
        ALL_KEYS.forEach(k => {
          gv[k] = extractKey(gi, k);
          bv[k] = extractKey(bi, k);
        });
        setGlobalVals(gv);
        setBrandVals(bv);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load settings.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId]);

  // Sync prompt editor when tab, scope, or stored values change
  useEffect(() => {
    if (tab === "models") return;
    const key = PROMPT_KEYS[tab];
    setEditorVal(isBrand ? (brandVals[key] || "") : (globalVals[key] || ""));
    if (tab === "chat") {
      const autoKey = PROMPT_KEYS.chat_auto;
      setEditorAutoVal(isBrand ? (brandVals[autoKey] || "") : (globalVals[autoKey] || ""));
      const tv = (isBrand ? brandVals[THRESHOLD_KEY] : globalVals[THRESHOLD_KEY]) || "";
      setThresholdVal(tv || "1");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, brandVals, globalVals, editScope]);

  // Sync model selectors when stored values or scope change
  useEffect(() => {
    setIndivModel(
      (isBrand ? brandVals[MODEL_KEYS.individual] : globalVals[MODEL_KEYS.individual]) || effectiveModel(MODEL_KEYS.individual)
    );
    setBulkModel(
      (isBrand ? brandVals[MODEL_KEYS.bulk] : globalVals[MODEL_KEYS.bulk]) || effectiveModel(MODEL_KEYS.bulk)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandVals, globalVals, editScope]);

  const upsert = async (key, value) => {
    await settingsUpsert({ scope_type: isBrand ? "brand" : "global", scope_id: isBrand ? brandId : null, key, value, value_type: "string" });
    if (isBrand) setBrandVals(prev => ({ ...prev, [key]: value }));
    else setGlobalVals(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (tab === "models") {
      setSaving("models");
      setErr(null);
      try {
        await Promise.all([
          upsert(MODEL_KEYS.individual, indivModel),
          upsert(MODEL_KEYS.bulk, bulkModel),
        ]);
        setSaved("models");
        setTimeout(() => setSaved(null), 2000);
      } catch (e) {
        setErr(e?.message || "Save failed.");
      } finally {
        setSaving(null);
      }
      return;
    }

    const key = activeKey;
    setSaving(key);
    setErr(null);
    try {
      if (tab === "chat") {
        await Promise.all([
          upsert(key, editorVal.trim()),
          upsert(PROMPT_KEYS.chat_auto, editorAutoVal.trim()),
          upsert(THRESHOLD_KEY, thresholdVal || "1"),
        ]);
      } else {
        await upsert(key, editorVal.trim());
      }
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setErr(e?.message || "Save failed.");
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async () => {
    if (!isBrand) return;
    if (tab === "models") {
      setSaving("models");
      setErr(null);
      try {
        await Promise.all([
          upsert(MODEL_KEYS.individual, ""),
          upsert(MODEL_KEYS.bulk, ""),
        ]);
        setIndivModel(effectiveModel(MODEL_KEYS.individual));
        setBulkModel(effectiveModel(MODEL_KEYS.bulk));
        setSaved("models");
        setTimeout(() => setSaved(null), 2000);
      } catch (e) {
        setErr(e?.message || "Reset failed.");
      } finally {
        setSaving(null);
      }
      return;
    }
    const key = activeKey;
    setSaving(key);
    setErr(null);
    try {
      if (tab === "chat") {
        await Promise.all([upsert(key, ""), upsert(PROMPT_KEYS.chat_auto, ""), upsert(THRESHOLD_KEY, "")]);
        setEditorAutoVal("");
        setThresholdVal("1");
      } else {
        await upsert(key, "");
      }
      setEditorVal("");
      setSaved(key);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setErr(e?.message || "Reset failed.");
    } finally {
      setSaving(null);
    }
  };

  if (!open) return null;

  const isSaving = saving !== null;
  const wasSaved = tab === "models" ? saved === "models" : saved === activeKey;
  const src = tab !== "models" ? source(activeKey) : null;
  const hasOverride = tab !== "models"
    ? (brandOverrides(activeKey) || (tab === "chat" && (brandOverrides(PROMPT_KEYS.chat_auto) || brandOverrides(THRESHOLD_KEY))))
    : isBrand && (brandOverrides(MODEL_KEYS.individual) || brandOverrides(MODEL_KEYS.bulk));
  const srcAuto = source(PROMPT_KEYS.chat_auto);
  const srcThreshold = source(THRESHOLD_KEY);

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 800,
        border: "1px solid",
        borderColor: tab === id ? "#6366f1" : "#e5e7eb",
        background: tab === id ? "#6366f1" : "#fff",
        color: tab === id ? "#fff" : "#374151",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  const labelStyle = { fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" };
  const modelSelectStyle = {
    width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 10px",
    fontSize: 13, fontWeight: 600, color: "#111827", background: "#fff", outline: "none",
  };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 9500, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.22)" }}
      >
        {/* Header */}
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ fontWeight: 900, fontSize: 15, color: "#111827" }}>AI Settings</div>
            <button onClick={onClose} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 8, padding: "5px 12px", cursor: "pointer", fontSize: 14, color: "#6b7280" }}>✕</button>
          </div>
          {/* Scope toggle — only when brandId is provided */}
          {brandId ? (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button
                onClick={() => setEditScope("global")}
                style={{
                  padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", border: "1px solid",
                  borderColor: editScope === "global" ? "#6366f1" : "#e5e7eb",
                  background: editScope === "global" ? "#6366f1" : "#fff",
                  color: editScope === "global" ? "#fff" : "#6b7280",
                }}
              >
                🌐 Global defaults
              </button>
              <button
                onClick={() => setEditScope("brand")}
                style={{
                  padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer", border: "1px solid",
                  borderColor: editScope === "brand" ? "#6366f1" : "#e5e7eb",
                  background: editScope === "brand" ? "#6366f1" : "#fff",
                  color: editScope === "brand" ? "#fff" : "#6b7280",
                }}
              >
                🏷 This brand only
              </button>
              <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 4, alignSelf: "center" }}>
                {editScope === "brand" ? "Overrides global for this brand" : "Applies to all brands without overrides"}
              </span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Global defaults — apply to all brands unless overridden per brand
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "18px 22px" }}>
          {loading ? (
            <div style={{ color: "#6b7280", fontWeight: 700, fontSize: 13, padding: "20px 0" }}>Loading settings…</div>
          ) : (
            <>
              {/* Tabs */}
              <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                {tabBtn("individual", "Individual / Bulk Prompt")}
                {tabBtn("chat", "Chat System Prompt")}
                {tabBtn("models", "⚙ Models")}
              </div>

              {/* Models tab */}
              {tab === "models" && (
                <div style={{ display: "grid", gap: 18 }}>
                  <div>
                    <label style={labelStyle}>Individual analysis model</label>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>
                      Used when clicking "✨ Analyze" on a single ad detail page.
                    </div>
                    <select value={indivModel} onChange={e => setIndivModel(e.target.value)} style={modelSelectStyle}>
                      {ANALYSIS_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Bulk analysis model</label>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>
                      Used when running "✨ Analyze Selected" on multiple ads.
                    </div>
                    <select value={bulkModel} onChange={e => setBulkModel(e.target.value)} style={modelSelectStyle}>
                      {ANALYSIS_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  </div>
                  <div style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12, color: "#6b7280" }}>
                    💬 Chat (MAD Pilot) model is selected per-session inside the chat drawer.
                  </div>
                  {isBrand && (
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>
                      Effective global models: Individual = <strong>{effectiveModel(MODEL_KEYS.individual)}</strong> · Bulk = <strong>{effectiveModel(MODEL_KEYS.bulk)}</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Prompt tabs (individual / chat) */}
              {tab !== "models" && (
                <>
                  {/* Source badge */}
                  <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 800, borderRadius: 6, padding: "2px 8px",
                      background: src === "brand" ? "#fef2f2" : src === "global" ? "#f0fdf4" : "#f8fafc",
                      color: src === "brand" ? "#dc2626" : src === "global" ? "#166534" : "#6b7280",
                      border: `1px solid ${src === "brand" ? "#fecaca" : src === "global" ? "#bbf7d0" : "#e5e7eb"}`,
                    }}>
                      {src === "brand" ? "Brand override" : src === "global" ? "Global default" : "Built-in default"}
                    </span>
                    {isBrand && !hasOverride && (
                      <span style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>
                        No brand override — using {src === "global" ? "global" : "built-in"} default
                      </span>
                    )}
                  </div>

                  {/* Prompt editor */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>
                      {tab === "individual" ? "Prompt template (use {ad_context} placeholder for ad data)" : "AI role / system prompt"}
                    </label>
                    <textarea
                      value={editorVal}
                      onChange={e => setEditorVal(e.target.value)}
                      placeholder={defaults[tab]}
                      rows={tab === "chat" ? 6 : 12}
                      style={{
                        width: "100%", resize: "vertical", fontFamily: "monospace",
                        fontSize: 12, lineHeight: 1.55, color: "#111827", fontWeight: 500,
                        border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px",
                        outline: "none", background: "#fafafa", boxSizing: "border-box",
                      }}
                    />
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                      Leave empty to use the {isBrand ? "global/built-in" : "built-in"} default.
                    </div>
                  </div>

                  {/* Auto message field — only shown in chat tab */}
                  {tab === "chat" && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                          ⚡ Auto Analysis Starting Message
                        </label>
                        {srcAuto === "brand" && (
                          <span style={{
                            fontSize: 11, fontWeight: 800, borderRadius: 6, padding: "2px 8px",
                            background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                          }}>
                            Brand override
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                        This is the message sent automatically when you click <strong>⚡ Auto Analysis</strong> in the chat drawer.
                      </div>
                      <textarea
                        value={editorAutoVal}
                        onChange={e => setEditorAutoVal(e.target.value)}
                        placeholder={DEFAULT_CHAT_AUTO_MESSAGE}
                        rows={5}
                        style={{
                          width: "100%", resize: "vertical", fontFamily: "monospace",
                          fontSize: 12, lineHeight: 1.55, color: "#111827", fontWeight: 500,
                          border: "1.5px solid #c7d2fe", borderRadius: 10, padding: "10px 12px",
                          outline: "none", background: "#fafafa", boxSizing: "border-box",
                        }}
                      />
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                        Leave empty to use the built-in default analysis prompt.
                      </div>
                    </div>
                  )}

                  {/* Cost alert threshold — chat tab only */}
                  {tab === "chat" && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                          💰 Cost Alert Threshold ($)
                        </label>
                        {srcThreshold === "brand" && (
                          <span style={{
                            fontSize: 11, fontWeight: 800, borderRadius: 6, padding: "2px 8px",
                            background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
                          }}>
                            Brand override
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                        Tracker turns red and a warning popup appears when the session cost reaches this amount.
                        Additional warnings appear at $5, $10, $20, $50 (if above the threshold).
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={thresholdVal}
                        onChange={e => setThresholdVal(e.target.value)}
                        style={{
                          width: 120, border: "1.5px solid #e5e7eb", borderRadius: 8,
                          padding: "7px 10px", fontSize: 13, fontWeight: 600,
                          color: "#111827", outline: "none", background: "#fafafa",
                        }}
                      />
                    </div>
                  )}

                  {/* Reference prompt */}
                  <ReferencePrompt label={
                    editorVal.trim()
                      ? "Reference: effective prompt if saved empty"
                      : "Effective prompt (will be used)"
                  } value={effectiveVal(tab)} />
                </>
              )}

              {err && <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 700, marginBottom: 10 }}>{err}</div>}
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div style={{ padding: "14px 22px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
            {isBrand && hasOverride && (
              <button onClick={handleReset} disabled={isSaving}
                style={{ fontSize: 12, padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#dc2626", cursor: "pointer", fontWeight: 700 }}>
                Reset to Global
              </button>
            )}
            <button onClick={onClose} className="btn-secondary"
              style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8 }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="btn-primary"
              style={{ fontSize: 13, padding: "8px 18px", borderRadius: 8, opacity: isSaving ? 0.7 : 1 }}>
              {isSaving ? "Saving…" : wasSaved ? "Saved ✓" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
